/**
 * PropertiesPanel Component
 * 
 * Displays and allows editing of the selected block's properties.
 * Shows different editors based on block type.
 */

import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useSelectedBlock, useMenuBuilderStore } from '@/stores/menuBuilderStore';
import { BlockType, getBlockMetadata } from '@/types/menuBuilder';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  X,
  LayoutTemplate,
  Grid3x3,
  Star,
  Image,
  Type,
  Minus,
  Space,
  Search,
  List
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Property editors for each block type
import { HeaderPropertiesEditor } from './HeaderPropertiesEditor';
import { CategoryPropertiesEditor } from './CategoryPropertiesEditor';
import { FeaturedPropertiesEditor } from './FeaturedPropertiesEditor';
import { BannerPropertiesEditor } from './BannerPropertiesEditor';
import { TextPropertiesEditor } from './TextPropertiesEditor';
import { DividerPropertiesEditor } from './DividerPropertiesEditor';
import { SpacerPropertiesEditor } from './SpacerPropertiesEditor';
import { SearchPropertiesEditor } from './SearchPropertiesEditor';
import { CategoryNavigationPropertiesEditor } from './CategoryNavigationPropertiesEditor';
import { GlobalThemeEditor } from './GlobalThemeEditor';

// Map block types to their Lucide icons
const BLOCK_ICONS: Record<BlockType, React.ComponentType<{ className?: string }>> = {
  header: LayoutTemplate,
  category: Grid3x3,
  featured: Star,
  banner: Image,
  text: Type,
  divider: Minus,
  spacer: Space,
  search: Search,
  'category-navigation': List,
};

// Map block types to their property editors
// Using React.ComponentType<any> is necessary here because each editor has different
// prop interfaces, but we dynamically select them based on the selected block type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PROPERTY_EDITORS: Record<BlockType, React.ComponentType<any>> = {
  header: HeaderPropertiesEditor,
  category: CategoryPropertiesEditor,
  featured: FeaturedPropertiesEditor,
  banner: BannerPropertiesEditor,
  text: TextPropertiesEditor,
  divider: DividerPropertiesEditor,
  spacer: SpacerPropertiesEditor,
  search: SearchPropertiesEditor,
  'category-navigation': CategoryNavigationPropertiesEditor,
};

interface PropertiesPanelProps {
  className?: string;
}

export const PropertiesPanel = memo(function PropertiesPanel({
  className,
}: PropertiesPanelProps) {
  const { t } = useTranslation();
  const selectedBlock = useSelectedBlock();
  const selectBlock = useMenuBuilderStore((s) => s.selectBlock);
  const updateBlockProps = useMenuBuilderStore((s) => s.updateBlockProps);

  const handleClose = useCallback(() => {
    selectBlock(null);
  }, [selectBlock]);

  const handleUpdateProps = useCallback(
    (props: Record<string, unknown>) => {
      if (selectedBlock) {
        updateBlockProps(selectedBlock.id, props);
      }
    },
    [selectedBlock, updateBlockProps]
  );

  if (!selectedBlock) {
    return (
      <div className={cn('bg-background border-l flex flex-col', className)}>
        <ScrollArea className="flex-1">
          <GlobalThemeEditor />
        </ScrollArea>
      </div>
    );
  }

  const metadata = getBlockMetadata(selectedBlock.type);
  const Icon = BLOCK_ICONS[selectedBlock.type];
  const PropertyEditor = PROPERTY_EDITORS[selectedBlock.type];

  if (!metadata) return null;

  return (
    <div className={cn('bg-background border-l flex flex-col', className)}>
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">{metadata.name}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {PropertyEditor && (
            <PropertyEditor
              props={selectedBlock.props}
              onUpdate={handleUpdateProps}
            />
          )}
        </div>
      </ScrollArea>
    </div>
  );
});

export default PropertiesPanel;
